import { useTranslation } from '../../i18n';
import React, { useEffect, useState } from 'react';
import { Checkbox, Input, Select, Space, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import sshKeyService from '../../services/sshKeyService';
import { checkPublicKeys } from '../../utils/sshKeygen';

const { Text } = Typography;

export const EMPTY_SSH_PICK = { key_ids: [], paste: '', save: true, key_only: false };

/** At least one saved key selected and/or a valid pasted key (and no invalid paste). */
export const sshPickValid = (v) => {
    const paste = checkPublicKeys(v?.paste);
    if (paste === 'private' || paste === 'invalid') return false;
    return (v?.key_ids || []).length > 0 || paste === 'ok';
};

/** Request fields for purchase / rebuild. */
export const sshPickPayload = (v) => {
    const data = { ssh_key_ids: v?.key_ids || [], ssh_key_only: !!v?.key_only };
    const paste = String(v?.paste || '').trim();
    if (paste) {
        data.ssh_public_key = paste;
        data.save_ssh_key = !!v?.save;
    }
    return data;
};

/**
 * Saved keys (multi-select) + paste a new key, "save to my keys", "key-only".
 * Controlled: value / onChange with the EMPTY_SSH_PICK shape.
 */
const SshKeyPicker = ({ value = EMPTY_SSH_PICK, onChange }) => {
    const { t } = useTranslation();
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const set = (patch) => onChange?.({ ...value, ...patch });

    useEffect(() => {
        let alive = true;
        sshKeyService.list().then((r) => {
            if (!alive) return;
            setLoading(false);
            if (r?.status === 'success') setKeys(r.data?.items || []);
        });
        return () => { alive = false; };
    }, []);

    const paste = checkPublicKeys(value.paste);
    const pasteError = paste === 'private' ? t('This is a private key. Paste the public key (.pub).') : paste === 'invalid' ? t('Invalid public key') : null;

    return (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Select
                mode="multiple"
                allowClear
                loading={loading}
                value={value.key_ids}
                onChange={(ids) => set({ key_ids: ids })}
                placeholder={t('Select my keys')}
                optionFilterProp="label"
                notFoundContent={loading ? null : t('No keys')}
                options={keys.map((k) => ({ value: k.id, label: `${k.name} · ${k.fingerprint}` }))}
                style={{ width: '100%' }}
            />
            <Input.TextArea
                value={value.paste}
                onChange={(e) => set({ paste: e.target.value })}
                placeholder={`${t('Paste a new public key')}  ssh-ed25519 AAAA…`}
                autoSize={{ minRows: 1, maxRows: 4 }}
                status={pasteError ? 'error' : undefined}
            />
            {pasteError && <Text type="danger" style={{ fontSize: 12 }}>{pasteError}</Text>}
            <Space wrap size={16}>
                {String(value.paste || '').trim() && (
                    <Checkbox checked={value.save} onChange={(e) => set({ save: e.target.checked })}>{t('Save to My Keys')}</Checkbox>
                )}
                <Checkbox checked={value.key_only} onChange={(e) => set({ key_only: e.target.checked })}>
                    {t('Disable password login')}
                    <Tooltip title={t('SSH accepts keys only; the VNC console still accepts the password')}>
                        <QuestionCircleOutlined style={{ marginInlineStart: 4, color: 'rgba(0,0,0,0.45)' }} />
                    </Tooltip>
                </Checkbox>
            </Space>
        </Space>
    );
};

export default SshKeyPicker;
